import type { FieldCovariate, ManifestLayer } from "../types/contract";
import { blobPropOf, depthSuffix, needsDepthConversion } from "./depthDisplay";
import { estimateAtDepth, cecAtDepth, omAtDepth } from "./depthEstimate";
import { displayUnitOf, lensedValue } from "./mapValues";
import { analyteOf, unitSuffix, type ConvertedValue } from "./soilUnits";
import { soilUnits } from "../stores/soilUnitsStore";
import { toPct, type BehaviorKey } from "./behaviorZones";

export type AutomapKind = "points" | "behavior" | "relative";
// `unitless`: the behavior-zone readings are indices — show the bare number, no unit. `scale`:
// rescale the RAW driver value onto the 0–100 percentile scale before display, so the preview
// card's behavior readings match the builder / customize page (which classify on the rescaled
// drivers).
export interface ReadingKey {
  key: string;
  label: string;
  unitless?: boolean;
  scale?: BehaviorKey;
}

// Apply a reading's optional percentile rescale (behavior drivers) to a raw value; passthrough otherwise.
export function scaledReading(r: ReadingKey, v: number | null): number | null {
  if (v == null || !Number.isFinite(v)) return v;
  return r.scale ? toPct(v, r.scale) : v;
}

// Format one covariate reading (mirrors lib/mapValues.formatLayerValue for the catalog row shape):
// null → nothing to show; categorical → class label; numeric → magnitude-based decimals + unit.
// `unitless` suppresses the unit suffix (for index readings that read best as a bare number).
export function previewReadingsAtDepth(
  keys: ReadingKey[],
  covById: Map<string, FieldCovariate>,
  props: Record<string, unknown> | undefined | null,
): Map<string, number> {
  const out = new Map<string, number>();
  const depth = soilUnits().depthIn;
  if (!needsDepthConversion(depth) || !props) return out; // 8" is an identity for every property

  const num = (k: string): number | undefined => {
    const raw = props[k];
    const n = typeof raw === "number" ? raw : raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const soil: { key: string; prop: string }[] = [];
  for (const r of keys) {
    const cat = covById.get(r.key)?.category;
    if (cat !== "model" && cat !== "automap") continue; // NOT behaviour/relative drivers — see note above
    const prop = blobPropOf(r.key);
    if (prop) soil.push({ key: r.key, prop });
  }
  const at = (prop: string) => soil.find((x) => x.prop === prop);
  const cec8 = at("cec") && num(at("cec")!.key);
  const om8 = at("om") && num(at("om")!.key);
  if (!Number.isFinite(cec8 as number) || !Number.isFinite(om8 as number))
    return out; // CEC is the chain root

  const blob = {
    cec8: cec8 as number,
    om8: om8 as number,
    ph: at("ph") ? num(at("ph")!.key) : undefined,
    p: at("p") ? num(at("p")!.key) : undefined,
    k: at("k") ? num(at("k")!.key) : undefined,
  };
  const est = estimateAtDepth(depth, blob);
  for (const { key, prop } of soil) {
    const v =
      prop === "cec"
        ? cecAtDepth(depth, blob.cec8)
        : prop === "om"
          ? omAtDepth(depth, blob.om8, blob.cec8)
          : prop === "p"
            ? est.p
            : prop === "k"
              ? est.k
              : prop === "ph"
                ? est.ph
                : null;
    if (v != null && Number.isFinite(v)) out.set(key, v);
  }
  return out;
}

export function fmtCov(
  cov: FieldCovariate | undefined,
  v: number | null,
  unitless = false,
  atDepth = false,
): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  if (cov?.categories && cov.categories.length > 0) {
    const e = cov.categories.find((c) => Number(c.value) === Math.round(v));
    return e ? e.label : `class ${Math.round(v)}`;
  }
  // `lensedValue` reads the layer's unit for the ppm→lbs/ac gate, so the covariate's unit must ride
  // along — and the printed unit must be the DISPLAY unit, or a converted number would sit
  // `atDepth` = whether `v` was actually depth-SOLVED (previewReadingsAtDepth answered for this
  // key).
  const lyr = {
    id: cov?.key ?? "",
    title: cov?.title ?? "",
    unit: cov?.unit,
  } as ManifestLayer;
  const lv = unitless ? null : lensedValue(lyr, v, atDepth);
  const shown = lv ? lv.value : v;
  const abs = Math.abs(shown);
  const t =
    abs >= 100
      ? shown.toFixed(0)
      : abs >= 10
        ? shown.toFixed(1)
        : shown.toFixed(2);
  if (unitless || !cov?.unit) return t;
  const isSoil = analyteOf(lyr.id ?? lyr.title) !== "other";
  const suffix = lv
    ? `${unitSuffix({ value: lv.value, extractant: lv.extractant, fellBack: lv.fellBack } as ConvertedValue)}${isSoil ? depthSuffix(soilUnits().depthIn, atDepth) : ""}`
    : "";
  return `${t} ${displayUnitOf(lyr)}${suffix}`;
}

// The "which point/zone" headline line from a hovered automap feature's properties.
export function automapHeadline(
  kind: AutomapKind,
  p: Record<string, unknown> | undefined,
): string | null {
  if (!p) return null;
  if (kind === "points") return p.num != null ? `Sample point ${p.num}` : null;
  if (kind === "behavior")
    return p.label != null ? `${p.label}${p.code ? ` (${p.code})` : ""}` : null;
  const z = p.zone_number ?? p.zone;
  return z != null ? `Zone ${z}` : null;
}

// The METHOD-SPECIFIC hover readings per school, resolved from the field's covariate catalog chose
// "method-specific" over a fixed set): High-Res/Grid → soil props; Behavior → the 3 drivers
// Relative → the zone-design drivers present.
export function findOmModel(
  covs: FieldCovariate[],
): FieldCovariate | undefined {
  return covs.find(
    (c) =>
      c.has_values !== false && c.category === "model" && /om/i.test(c.key),
  );
}

// The Classic-Grid OM IDW map: the OM model sampled at the 2.5-ac grid points then IDW-interpolated
// (built at covariate-build time; canonical key `automap__grid_om_idw`).
export function findGridOmIdw(
  covs: FieldCovariate[],
): FieldCovariate | undefined {
  return covs.find(
    (c) =>
      c.has_values !== false &&
      /grid.*om.*idw|grid_om_idw|om_grid_idw/i.test(c.key),
  );
}

// The Classic-Grid IDW READINGS: all 5 uncorrected models sampled at the grid points +
// IDW-interpolated — `automap__grid_{om,ph,p,k,cec}_idw`.
export function resolveGridIdwReadings(covs: FieldCovariate[]): ReadingKey[] {
  const family = covs.filter(
    (c) => c.has_values !== false && /grid.*idw|grid_.*_idw/i.test(c.key),
  );
  const pick = (re: RegExp) => family.find((c) => re.test(c.key));
  const defs: [RegExp, string][] = [
    [/om/i, "OM"],
    [/ph/i, "pH"],
    [/(^|_)p(_|$)/i, "P"],
    [/(^|_)k(_|$)/i, "K"],
    [/cec/i, "CEC"],
  ];
  const out: ReadingKey[] = [];
  for (const [re, label] of defs) {
    const c = pick(re);
    if (c) out.push({ key: c.key, label });
  }
  return out;
}

export function resolveReadingKeys(covs: FieldCovariate[]): {
  soil: ReadingKey[];
  behavior: ReadingKey[];
  drivers: ReadingKey[];
} {
  const has = (k: string) =>
    covs.some((c) => c.key === k && c.has_values !== false);
  // uncorrected soil-property models — matched by category='model' + a nutrient regex so BOTH the live
  // `model__uncorrected_{om,ph,p,k,cec}` and the mock `model__{om_pct,ph,p_ppm,k_ppm,cec}` resolve.
  const modelCov = (re: RegExp) =>
    covs.find(
      (x) => x.has_values !== false && x.category === "model" && re.test(x.key),
    );
  const soilDefs: [RegExp, string][] = [
    [/om/i, "OM"],
    [/ph/i, "pH"],
    [/(^|_)p(_|$)/i, "P"],
    [/(^|_)k(_|$)/i, "K"],
    [/cec/i, "CEC"],
  ];
  const soil: ReadingKey[] = [];
  for (const [re, label] of soilDefs) {
    const c = modelCov(re);
    if (c) soil.push({ key: c.key, label });
  }
  // Behavior-zone readings are unitless indices: short names ("Moisture Index" "Production Index"
  // "Fertility Index"), no units — the same 3 driver layers the zone builder uses.
  const behaviorAll: ReadingKey[] = [
    {
      key: has("diffusion__corr_k_variance")
        ? "diffusion__corr_k_variance"
        : "diffusion__pre_k_variance",
      label: "Moisture Index",
      unitless: true,
      scale: "moisture",
    },
    {
      key: "productivity__index_vs_county",
      label: "Production Index",
      unitless: true,
      scale: "productivity",
    },
    {
      key: has("fertility__index_post")
        ? "fertility__index_post"
        : "fertility__index_pre",
      label: "Fertility Index",
      unitless: true,
      scale: "fertility",
    },
  ];
  const behavior = behaviorAll.filter((r) => has(r.key));
  // relative-zone "main drivers" — the zone-design covariates present on this field, curated to a few
  const pick = (re: RegExp, cat: string, label: string): ReadingKey | null => {
    const c = covs.find(
      (x) => x.has_values !== false && x.category === cat && re.test(x.key),
    );
    return c ? { key: c.key, label } : null;
  };
  const drivers = [
    pick(/ndvi/i, "satellite", "NDVI"),
    pick(/twi|wet/i, "topography", "Wetness"),
    pick(/clay/i, "ssurgo", "Clay"),
    pick(/yield/i, "machine", "Yield"),
  ].filter((r): r is ReadingKey => r != null);
  return { soil, behavior, drivers };
}
